import { parseAmount, roundUpItemIncGst } from './cost-math.js';

const DEFAULT_GST_RATE = 0.10;

function roundCents(value) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function roundWholeDollars(value) {
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : 0;
}

/**
 * Round a manually edited client item in its current display basis while
 * preserving QuoteMaker's clean inclusive-GST line-price contract.
 *
 * Inclusive displays round directly. Exclusive displays first choose the
 * clean inclusive-GST target, then return its ex-GST display equivalent.
 */
export function roundQuoteItemDisplayAmountUp(displayAmount, options = {}) {
    const requestedGstRate = Number(options.gstRate);
    const gstRate = options.gstExempt === true
        ? 0
        : Number.isFinite(requestedGstRate) && requestedGstRate >= 0
            ? requestedGstRate
            : DEFAULT_GST_RATE;
    const isGstInclusive = options.isGstInclusive !== false;
    const requestedUnit = Number(options.unitDollars);
    const unitDollars = Number.isFinite(requestedUnit) && requestedUnit > 0
        ? requestedUnit
        : 10;
    const amount = parseAmount(displayAmount);
    const amountIncGst = isGstInclusive ? amount : amount * (1 + gstRate);
    const roundedIncGst = roundUpItemIncGst(amountIncGst, unitDollars);

    if (isGstInclusive) return roundedIncGst;

    // An ex-GST display amount is stored to cents, and GST is independently
    // rounded to cents. Therefore some nominal clean inc-GST targets cannot be
    // represented exactly: $954.55 + 10% GST is $1,050.01, not $1,050. Search
    // upwards for the first two-decimal ex-GST amount whose derived client
    // total is exactly on the configured clean-line increment.
    const minimumExGstCents = Math.round(amount * 100);
    for (let increment = 0; increment < 1_000; increment += 1) {
        const targetIncGst = roundCents(roundedIncGst + (increment * unitDollars));
        const centreCents = Math.round((targetIncGst / (1 + gstRate)) * 100);

        for (let offset = -2; offset <= 2; offset += 1) {
            const candidateCents = centreCents + offset;
            if (candidateCents < minimumExGstCents || candidateCents < 0) continue;
            const candidateExGst = candidateCents / 100;
            const candidateTotal = deriveQuoteDisplayTotalsFromAmount(candidateExGst, {
                gstRate,
                isGstInclusive: false,
            }).totalIncGst;
            if (Math.round(candidateTotal * 100) === Math.round(targetIncGst * 100)) {
                return candidateExGst;
            }
        }
    }

    // Standard Australian GST always resolves in the search above. This
    // defensive fallback still rounds upwards and never returns less than the
    // builder-entered ex-GST value if a future invalid rate reaches the helper.
    return Math.max(amount, roundCents(roundedIncGst / (1 + gstRate)));
}

export function sumScopeDisplayAmounts(scopeItems = []) {
    if (!Array.isArray(scopeItems)) return 0;
    return roundCents(scopeItems.reduce((sum, item) => {
        return sum + parseAmount(item?.amount ?? item?.total ?? item?.price);
    }, 0));
}

export function deriveQuoteDisplayTotalsFromAmount(displayAmount, options = {}) {
    const gstRate = Number.isFinite(Number(options.gstRate))
        ? Number(options.gstRate)
        : DEFAULT_GST_RATE;
    const isGstInclusive = options.isGstInclusive !== false;
    const round = options.wholeDollars ? roundWholeDollars : roundCents;
    const displayTotal = round(parseAmount(displayAmount));

    let subtotalExGst;
    let gstAmount;
    let totalIncGst;

    if (isGstInclusive) {
        totalIncGst = displayTotal;
        subtotalExGst = round(totalIncGst / (1 + gstRate));
        gstAmount = round(totalIncGst - subtotalExGst);
    } else {
        subtotalExGst = displayTotal;
        gstAmount = round(subtotalExGst * gstRate);
        totalIncGst = round(subtotalExGst + gstAmount);
    }

    return {
        displayTotal,
        subtotalExGst,
        gstAmount,
        totalIncGst,
        isGstInclusive,
        gstRate,
        itemAmountBasis: isGstInclusive ? 'inc_gst' : 'ex_gst',
    };
}

/** A row that attracts no GST (the HBCF premium is stored this way). */
export function isGstExemptScopeItem(item) {
    return item?.gstExempt === true || item?.gst_exempt === true;
}

/**
 * The quote's totals from its items, in their display basis. Stage 4, Stage 5
 * and the nav all read this one function, so they cannot disagree. GST is
 * worked line by line, to the cent, as the Stage 5 quote has always printed
 * it (cost-math's per-line GST): an inc-GST line's ex-GST part is its amount
 * over 1 + GST, rounded, and the rest is GST; an ex-GST line adds its GST,
 * rounded. A GST-exempt row (the HBCF premium) adds its amount to the
 * subtotal and the total and no GST. On an inc-GST quote the total is always
 * the sum of the amounts. `wholeDollars` (the nav) rounds the three results.
 */
export function deriveQuoteDisplayTotalsFromScope(scopeItems = [], options = {}) {
    const rows = Array.isArray(scopeItems) ? scopeItems : [];
    const requestedGstRate = Number(options.gstRate);
    const gstRate = Number.isFinite(requestedGstRate) && requestedGstRate >= 0 ? requestedGstRate : DEFAULT_GST_RATE;
    const isGstInclusive = options.isGstInclusive !== false;
    let exCents = 0;
    let gstCents = 0;
    let exemptCents = 0;
    for (const row of rows) {
        const amountCents = Math.round(parseAmount(row?.amount ?? row?.total ?? row?.price) * 100);
        if (isGstExemptScopeItem(row)) {
            exCents += amountCents;
            exemptCents += amountCents;
        } else if (isGstInclusive) {
            const lineExCents = Math.round(amountCents / (1 + gstRate));
            exCents += lineExCents;
            gstCents += amountCents - lineExCents;
        } else {
            exCents += amountCents;
            gstCents += Math.round(amountCents * gstRate);
        }
    }
    let subtotalExGst = exCents / 100;
    let totalIncGst = (exCents + gstCents) / 100;
    if (options.wholeDollars) {
        subtotalExGst = roundWholeDollars(subtotalExGst);
        totalIncGst = roundWholeDollars(totalIncGst);
    }
    return {
        displayTotal: isGstInclusive ? totalIncGst : subtotalExGst,
        subtotalExGst,
        gstAmount: roundCents(totalIncGst - subtotalExGst),
        totalIncGst,
        isGstInclusive,
        gstRate,
        itemAmountBasis: isGstInclusive ? 'inc_gst' : 'ex_gst',
        gstExemptAmount: exemptCents / 100,
    };
}
